import React, { useEffect } from "react";
import { Box, Button, Typography, CircularProgress, Grid, Paper, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { join_as_agent, join_as_vendor, service_provider } from "../../api/Role";
import { get_profile, get_settings } from "../../api/ControllApi";
import { toast } from "react-toastify";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const Dashboard = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [loadingRole, setLoadingRole] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [settings, setSettings] = useState(null);

  const buttons = [
    { name: "Join As Agent", img: "/assets/icons/join_agent.png", role: "agent" },
    { name: "Join As Builder/Promoters", img: "/assets/icons/join_vendor.png", role: "vendor" },
    { name: "Join As Material/Service Provider", img: "/assets/icons/service_provider.png", role: "service" },
  ];

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        const [profileRes, settingsRes] = await Promise.all([
          get_profile(),
          get_settings()
        ]);
        setProfileData(profileRes?.data || null);
        setSettings(settingsRes?.data || null);
      } catch (err) {

        // toast.error("Failed to load dashboard data");
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, []);

  const handleClick = async (role) => {
    try {
      setLoadingRole(role);
      let response;

      if (role === "agent") {
        response = await join_as_agent();
      } else if (role === "vendor") {
        response = await join_as_vendor();
      } else if (role === "service") {
        response = await service_provider();
      }


      if (response?.status === 200) {
        toast.success(response?.message || "Request sent successfully!");
      } else {
        toast.error(response?.message || "Something went wrong!");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to connect to server!");
    }
    finally {
      setLoadingRole(null);
    }
  };

  const reachableAmount = parseFloat(profileData?.cumulative_amount) || 0;
  const targetAmount = parseFloat(settings?.cumulative_max_cost) || 0;
  const progress = targetAmount > 0 ? Math.min(100, (reachableAmount / targetAmount) * 100) : 0;

  if (loadingData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
          mb: 4
        }}
      >
        {buttons.map((btn) => {
          const isAgent = btn.role === "agent" && Number(profileData?.is_agent) === 1;
          const isVendor = btn.role === "vendor" && Number(profileData?.is_vendor) === 1;
          const isService = btn.role === "service" && Number(profileData?.is_services) === 1;

          const agentPending = btn.role === "agent" && Number(profileData?.agent_request) === 1;
          const vendorPending = btn.role === "vendor" && Number(profileData?.vendor_request) === 1;
          const servicePending = btn.role === "service" && Number(profileData?.service_request) === 1;

          const hasRole = isAgent || isVendor || isService;
          const isPending = agentPending || vendorPending || servicePending;
          const shouldDisable = hasRole || isPending;

          const getLabel = () => {
            if (isAgent) return "You Are Agent";
            if (isVendor) return "You Are Builder/Promoters";
            if (isService) return "You Are Material/Service Provider";

            if (agentPending) return "Agent Request Pending";
            if (vendorPending) return "Builder/Promoters Request Pending";
            if (servicePending) return "Material/Service Provider Request Pending";

            return btn.name;
          };

          return (
            <Button
              key={btn.name}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
                border: "1px solid #ccc",
                borderRadius: 2,
                padding: 2,
                background: "white",
                ":hover": { background: shouldDisable ? "white" : "#eee" },
                width: { xs: "100%", sm: "auto" },
                justifyContent: "flex-start",
                cursor: shouldDisable ? "default" : "pointer",
                textTransform: "none",
              }}
              disabled={loadingRole === btn.role || shouldDisable}
              onClick={() => !shouldDisable && handleClick(btn.role)}
            >
              <img
                src={btn.img}
                alt={btn.name}
                style={{ width: 60, height: 60 }}
              />

              {loadingRole === btn.role ? (
                <CircularProgress color="primary" size={20} />
              ) : (
                getLabel()
              )}
            </Button>
          );
        })}
      </Box>

      {/* Referral Code Section */}
      {(Number(profileData?.is_vendor) === 1 || Number(profileData?.is_agent) === 1) && profileData?.code && (
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2.5,
            borderRadius: 4,
            backgroundColor: "#f0fdf4", // Soft green background
            border: "1px dashed #4ade80",
            mb: 4,
            boxShadow: "0 2px 10px rgba(0,0,0,0.03)"
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: "#166534", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2 }}>
              Your Referral Code
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 900, color: "#166534", mt: 0.5 }}>
              {profileData.code}
            </Typography>
          </Box>
          <Button
            startIcon={<ContentCopyIcon />}
            variant="contained"
            disableElevation
            onClick={() => {
              navigator.clipboard.writeText(profileData.code);
              toast.success("Referral Code copied!");
            }}
            sx={{
              borderRadius: 3,
              px: 3,
              textTransform: "none",
              backgroundColor: "#166534",
              "&:hover": { backgroundColor: "#14532d" },
              fontWeight: 700
            }}
          >
            Copy Code
          </Button>
        </Paper>
      )}

      {/* Graph and Stats Section (Matched to Image) */}
      <Box sx={{ width: "100%", mt: 5 }}>
        {/* Note : */}
        {settings?.cumulative_description && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 3,
              backgroundColor: "#fffdf0", // Soft cream background
              border: "1px solid #f9ebbe",
              display: "flex",
              alignItems: "flex-start",
              gap: 1.5,
              mx: "auto",
              boxShadow: "0 2px 8px rgba(0,0,0,0.03)"
            }}
          >
            <InfoOutlinedIcon sx={{ color: "#d4a017", mt: 0.2, fontSize: "1.2rem" }} />
            <Typography
              variant="body2"
              sx={{
                color: "#856404",
                fontWeight: 500,
                lineHeight: 1.6,
                fontSize: "0.85rem",
                whiteSpace: "pre-line"
              }}
            >
              <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                Note
                <br />
              </Box>

              {settings.cumulative_description.split("\r\n").map((line, i) => {
                const [title, ...rest] = line.split(":");
                const content = rest.join(":");

                return (
                  <span key={i}>
                    {title && <strong>{title}:</strong>} {content}
                    {"\n"}
                  </span>
                );
              })}
            </Typography>
          </Box>
        )}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            borderRadius: 4,
            border: "1px solid #e0e0e0",
            backgroundColor: "#fff",
            boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
            width: "100%"
          }}
        >
          {/* Reachable Amount Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: "#444",
                fontSize: { xs: "1rem", sm: "1.2rem" },
              }}
            >
              Total Gioo Nano Plots Purchased
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: theme.palette.primary.main,
                fontSize: { xs: "1rem", sm: "1.2rem" },
                ml: 2
              }}
            >
              ₹ {reachableAmount.toLocaleString()}
            </Typography>
          </Box>

          {/* Target Goal Row */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              gap: 2
            }}
          >
            <Typography
              sx={{
                fontWeight: 600,
                color: "#444",
                fontSize: { xs: "1rem", sm: "1.2rem" },
              }}
            >
              Target Goal
            </Typography>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#444",
                fontSize: { xs: "1rem", sm: "1.2rem" },
                ml: 2
              }}
            >
              ₹ {targetAmount.toLocaleString()}
            </Typography>
          </Box>

          {/* Progress Bar Container */}
          <Box
            sx={{
              width: "100%",
              height: 16,
              backgroundColor: "#f0f2f5",
              borderRadius: 8,
              overflow: "hidden",
              position: "relative",
              mb: 4,
              boxShadow: "inset 0 1px 3px rgba(0,0,0,0.1)"
            }}
          >
            <Box
              sx={{
                width: `${progress}%`,
                height: "100%",
                backgroundColor: theme.palette.primary.main,
                borderRadius: 8,
                transition: "width 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </Box>

          {/* Percentage Badge */}
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Box
              sx={{
                backgroundColor: "#d32f2f",
                color: "white",
                px: 3,
                py: 0.75,
                borderRadius: "24px",
                fontSize: "0.95rem",
                fontWeight: 700,
                boxShadow: "0 2px 6px rgba(211, 47, 47, 0.3)"
              }}
            >
              {`${progress.toFixed(0)}%`} Done
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Dashboard;
